import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { logout } from '@ideaflow/ui/login'
import { getConfig } from '@ideaflow/ui/utils/config'

import { byCode, getByUserId } from '@/api/base'

// 用户设置类型
export interface UserSetting {
  dyeing_20?: string
  shutUp?: string
  genderPreference?: string
  dyeing_02?: string
  young?: string
  city?: string
  dyeing_21?: string
  dyeing_22?: string
  dyeing_05?: string
  dyeing_06?: string
  dyeing_03?: string
  dyeing_04?: string
  dyeing_09?: string
  dyeing_07?: string
  dyeing_08?: string
  dyeing_12?: string
  dyeing_10?: string
  pipeChat?: string
  dyeing_11?: string
  ynHistoryPipe?: string
  ynBgm?: string
  dyeing_16?: string
  dyeing_17?: string
  dyeing_18?: string
  dyeing_19?: string
  [key: string]: string | undefined
}

// 用户账户映射类型
export interface AccountMap {
  tourist?: string
  [key: string]: string | undefined
}

// 用户信息类型
export interface UserInfo {
  avatar?: string
  enabled?: number
  setting?: UserSetting
  token?: string
  uid?: string
  accountMap?: AccountMap
  identity?: number
  ynNew?: number
  nickname?: string
  createdTime?: number
  hasCertification?: number
  id?: string
  sysRoles?: string[]
}

export const useUserStore = defineStore(
  'user',
  () => {
    // 用户信息
    const userInfo = ref<UserInfo | null>(null)
    // 媒体白名单用户ID列表
    const uids = ref<string[]>([])
    // 是否可以访问电台（根据身份 identity 判断）
    const canAccessRadio = ref(false)
    // 是否可以访问视频（根据身份 identity 判断，v 开头）
    const canAccessVideo = ref(false)
    const token = computed(() => userInfo.value?.token || '')
    const id = computed(() => userInfo.value?.id || '')

    // 设置用户信息
    const setUserInfo = (info: UserInfo) => {
      userInfo.value = info
    }

    // 更新用户信息（部分更新）
    const updateUserInfo = (partialInfo: Partial<UserInfo>) => {
      if (userInfo.value) {
        userInfo.value = { ...userInfo.value, ...partialInfo }
      } else {
        userInfo.value = partialInfo as UserInfo
      }
    }

    const resetUserInfo = () => {
      userInfo.value = null
      uids.value = []
      canAccessRadio.value = false
      canAccessVideo.value = false
    }

    const performLogout = async () => {
      await logout()
    }

    // 清除用户信息
    const clearUserInfo = async () => {
      resetUserInfo()
      await performLogout()
    }

    // 判断是否已登录
    const isLoggedIn = () => {
      return !!userInfo.value?.token
    }

    // Getter 方法
    const getAvatar = () => {
      return userInfo.value?.avatar || ''
    }

    const getEnabled = () => {
      return userInfo.value?.enabled ?? 0
    }

    const getSetting = () => {
      return userInfo.value?.setting || {}
    }

    const getToken = () => {
      return token.value
    }

    const getUid = () => {
      return userInfo.value?.uid || ''
    }

    const getAccountMap = () => {
      return userInfo.value?.accountMap || {}
    }

    const getIdentity = () => {
      return userInfo.value?.identity ?? 0
    }

    const getYnNew = () => {
      return userInfo.value?.ynNew ?? 0
    }

    const getNickname = () => {
      return userInfo.value?.nickname || ''
    }

    const getCreatedTime = () => {
      return userInfo.value?.createdTime ?? 0
    }

    const getHasCertification = () => {
      return userInfo.value?.hasCertification ?? 0
    }

    const getId = () => {
      return id.value
    }

    const getSysRoles = () => {
      return userInfo.value?.sysRoles || []
    }

    // 获取媒体白名单用户ID列表
    const fetchMediaWhiteUids = async (code: string = 'MEDIA_WHITE') => {
      try {
        const result = await byCode(code)
        if (result.code === 0 && result.data?.uid) {
          uids.value = result.data.uid
          console.log('[store] 媒体白名单用户ID列表已更新:', uids.value.length)
        } else {
          console.error('[store] 获取媒体白名单失败:', result.msg)
          uids.value = []
        }
      } catch (error) {
        console.error('[store] 获取媒体白名单异常:', error)
        uids.value = []
      }
    }

    // 通过用户id获取身份，多个用,隔开
    const fetchIdentityByUserIds = async () => {
      try {
        const result = await getByUserId()
        if (result.code === 0 && result.data !== undefined && result.data !== null) {
          const identity = String(result.data)
          canAccessRadio.value = /\br\d*/i.test(identity || '')
          canAccessVideo.value = /\bv\d*/i.test(identity || '')
          console.log('[store] canAccessRadio 已更新:', canAccessRadio.value)
          console.log('[store] canAccessVideo 已更新:', canAccessVideo.value)
          return identity
        } else {
          console.error('[store] 获取用户身份失败:', result.msg)
          canAccessRadio.value = false
          canAccessVideo.value = false
          return ''
        }
      } catch (error) {
        console.error('[store] 获取用户身份异常:', error)
        canAccessRadio.value = false
        canAccessVideo.value = false
        return ''
      }
    }

    return {
      // 状态
      userInfo,
      uids,
      canAccessRadio,
      canAccessVideo,
      token,
      id,
      // 方法
      setUserInfo,
      updateUserInfo,
      resetUserInfo,
      performLogout,
      clearUserInfo,
      isLoggedIn,
      fetchMediaWhiteUids,
      fetchIdentityByUserIds,
      // Getter 方法
      getAvatar,
      getEnabled,
      getSetting,
      getToken,
      getUid,
      getAccountMap,
      getIdentity,
      getYnNew,
      getNickname,
      getCreatedTime,
      getHasCertification,
      getId,
      getSysRoles,
    }
  },
  {
    persist: {
      key: getConfig('USER_INFO_KEY') as string,
      storage: localStorage,
      paths: ['userInfo'], // 只持久化 userInfo，排除 uids
    },
  },
)
